import React, { useState, useRef } from 'react';
import { CampaignData, ChatMessage, FilterStatus, SortField, SortDirection, PendingChanges } from '../types';
import { UICampaignRecipient } from '../../../../utils/campaignAdapter';

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
}

export interface CampaignStateRefs {
  chatInputRef: React.RefObject<HTMLInputElement>;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  searchInputRef: React.RefObject<HTMLInputElement>;
}

export interface CampaignState {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  campaign: CampaignData | null;
  setCampaign: React.Dispatch<React.SetStateAction<CampaignData | null>>;

  isEditingName: boolean;
  setIsEditingName: React.Dispatch<React.SetStateAction<boolean>>;
  editName: string;
  setEditName: React.Dispatch<React.SetStateAction<string>>;
  showMoreMenu: boolean;
  setShowMoreMenu: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteConfirm: boolean;
  setShowDeleteConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  showAddRecipientsModal: boolean;
  setShowAddRecipientsModal: React.Dispatch<React.SetStateAction<boolean>>;

  filterStatus: FilterStatus;
  setFilterStatus: React.Dispatch<React.SetStateAction<FilterStatus>>;
  showFilterMenu: boolean;
  setShowFilterMenu: React.Dispatch<React.SetStateAction<boolean>>;
  expandedRecipients: Set<string>;
  setExpandedRecipients: React.Dispatch<React.SetStateAction<Set<string>>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  isSearchExpanded: boolean;
  setIsSearchExpanded: React.Dispatch<React.SetStateAction<boolean>>;

  sortField: SortField | null;
  setSortField: React.Dispatch<React.SetStateAction<SortField | null>>;
  sortDirection: SortDirection;
  setSortDirection: React.Dispatch<React.SetStateAction<SortDirection>>;

  isEditingEmail: boolean;
  setIsEditingEmail: React.Dispatch<React.SetStateAction<boolean>>;
  editSubject: string;
  setEditSubject: React.Dispatch<React.SetStateAction<string>>;
  editBody: string;
  setEditBody: React.Dispatch<React.SetStateAction<string>>;
  selectedTemplateId: string | null;
  setSelectedTemplateId: React.Dispatch<React.SetStateAction<string | null>>;

  isSettingsExpanded: boolean;
  setIsSettingsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  editSendWindowStart: string;
  setEditSendWindowStart: React.Dispatch<React.SetStateAction<string>>;
  editSendWindowEnd: string;
  setEditSendWindowEnd: React.Dispatch<React.SetStateAction<string>>;
  editSendDays: string[];
  setEditSendDays: React.Dispatch<React.SetStateAction<string[]>>;
  selectedEmailAccount: string;
  setSelectedEmailAccount: React.Dispatch<React.SetStateAction<string>>;
  selectedTimezone: string;
  setSelectedTimezone: React.Dispatch<React.SetStateAction<string>>;

  isChatOpen: boolean;
  setIsChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chatHasAnimatedIn: boolean;
  setChatHasAnimatedIn: React.Dispatch<React.SetStateAction<boolean>>;
  chatInput: string;
  setChatInput: React.Dispatch<React.SetStateAction<string>>;
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  isChatLoading: boolean;
  setIsChatLoading: React.Dispatch<React.SetStateAction<boolean>>;
  hasShownRecommendations: boolean;
  setHasShownRecommendations: React.Dispatch<React.SetStateAction<boolean>>;
  typingText: string;
  setTypingText: React.Dispatch<React.SetStateAction<string>>;
  isTypingComplete: boolean;
  setIsTypingComplete: React.Dispatch<React.SetStateAction<boolean>>;

  hoveredEngagement: string | null;
  setHoveredEngagement: React.Dispatch<React.SetStateAction<string | null>>;

  showChangeConfirmation: boolean;
  setShowChangeConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  pendingChanges: PendingChanges | null;
  setPendingChanges: React.Dispatch<React.SetStateAction<PendingChanges | null>>;

  showSendConfirmation: boolean;
  setShowSendConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  isActivating: boolean;
  setIsActivating: React.Dispatch<React.SetStateAction<boolean>>;

  recipients: UICampaignRecipient[];
  setRecipients: React.Dispatch<React.SetStateAction<UICampaignRecipient[]>>;
  recipientsLoading: boolean;
  setRecipientsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  recipientsTotal: number;
  setRecipientsTotal: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  selectedRecipientIds: Set<string>;
  setSelectedRecipientIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  recipientsVersion: number;
  incrementRecipientsVersion: () => void;

  emailTemplates: EmailTemplate[];
  setEmailTemplates: React.Dispatch<React.SetStateAction<EmailTemplate[]>>;
  emailTemplatesLoading: boolean;
  setEmailTemplatesLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCampaignState(): CampaignState & { refs: CampaignStateRefs } {
  const chatInputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [campaign, setCampaign] = useState<CampaignData | null>(null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddRecipientsModal, setShowAddRecipientsModal] = useState(false);

  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [expandedRecipients, setExpandedRecipients] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [editSendWindowStart, setEditSendWindowStart] = useState('');
  const [editSendWindowEnd, setEditSendWindowEnd] = useState('');
  const [editSendDays, setEditSendDays] = useState<string[]>([]);
  const [selectedEmailAccount, setSelectedEmailAccount] = useState('acc-1');
  const [selectedTimezone, setSelectedTimezone] = useState('America/New_York');

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatHasAnimatedIn, setChatHasAnimatedIn] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [hasShownRecommendations, setHasShownRecommendations] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  const [hoveredEngagement, setHoveredEngagement] = useState<string | null>(null);

  const [showChangeConfirmation, setShowChangeConfirmation] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges | null>(null);

  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const [recipients, setRecipients] = useState<UICampaignRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string>>(new Set());
  const [recipientsVersion, setRecipientsVersion] = useState(0);
  const incrementRecipientsVersion = () => setRecipientsVersion(v => v + 1);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);

  return {
    refs: {
      chatInputRef,
      chatContainerRef,
      searchInputRef,
    },
    isLoading, setIsLoading,
    campaign, setCampaign,
    isEditingName, setIsEditingName,
    editName, setEditName,
    showMoreMenu, setShowMoreMenu,
    showDeleteConfirm, setShowDeleteConfirm,
    showAddRecipientsModal, setShowAddRecipientsModal,
    filterStatus, setFilterStatus,
    showFilterMenu, setShowFilterMenu,
    expandedRecipients, setExpandedRecipients,
    searchQuery, setSearchQuery,
    isSearchExpanded, setIsSearchExpanded,
    sortField, setSortField,
    sortDirection, setSortDirection,
    isEditingEmail, setIsEditingEmail,
    editSubject, setEditSubject,
    editBody, setEditBody,
    selectedTemplateId, setSelectedTemplateId,
    isSettingsExpanded, setIsSettingsExpanded,
    editSendWindowStart, setEditSendWindowStart,
    editSendWindowEnd, setEditSendWindowEnd,
    editSendDays, setEditSendDays,
    selectedEmailAccount, setSelectedEmailAccount,
    selectedTimezone, setSelectedTimezone,
    isChatOpen, setIsChatOpen,
    chatHasAnimatedIn, setChatHasAnimatedIn,
    chatInput, setChatInput,
    chatMessages, setChatMessages,
    isChatLoading, setIsChatLoading,
    hasShownRecommendations, setHasShownRecommendations,
    typingText, setTypingText,
    isTypingComplete, setIsTypingComplete,
    hoveredEngagement, setHoveredEngagement,
    showChangeConfirmation, setShowChangeConfirmation,
    pendingChanges, setPendingChanges,
    showSendConfirmation, setShowSendConfirmation,
    isActivating, setIsActivating,
    recipients, setRecipients,
    recipientsLoading, setRecipientsLoading,
    recipientsTotal, setRecipientsTotal,
    currentPage, setCurrentPage,
    selectedRecipientIds, setSelectedRecipientIds,
    recipientsVersion, incrementRecipientsVersion,
    emailTemplates, setEmailTemplates,
    emailTemplatesLoading, setEmailTemplatesLoading,
  };
}
